import React from 'react';
import { ArrowRight, CheckCircle2, Shield, FileText, CheckSquare, Inbox as InboxIcon, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { HeroMock } from '../components/mocks/HeroMock';
import { PainMock } from '../components/mocks/PainMock';
import { getLink } from '../config/links';

export const LandingPage: React.FC = () => {
  const tourLink = getLink('tour');
  const signupLink = getLink('signup');
  
  return (
    <div className="flex flex-col min-h-screen bg-background text-ink font-sans">
      
      {/* SECTION A: HERO */}
      <section className="relative pt-28 pb-14 md:pt-40 md:pb-24 px-4 overflow-hidden border-b border-border/40">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          <div className="max-w-xl relative z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 mb-6 text-xs font-semibold tracking-wide uppercase bg-white text-muted rounded-full border border-border shadow-sm">
              <span className="w-2 h-2 rounded-full bg-accent animate-pulse"></span>
              Sistema operacional do follow-up
            </div>
            
            <h1 className="font-display font-extrabold text-5xl md:text-6xl text-ink leading-[1.05] tracking-tight mb-6">
              Feche o follow-up no <span className="text-primary underline decoration-primary/20 decoration-4 underline-offset-4">mesmo dia.</span>
            </h1>
            
            <p className="text-lg md:text-xl text-muted leading-relaxed mb-8 max-w-lg">
              Capture qualquer nota — reunião, ideia ou decisão — e transforme em execução com dono e prazo.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 mb-8">
              <Button href={tourLink} size="lg" className="gap-2 group shadow-lg shadow-primary/20">
                Ver meu Brief em 60s
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button href={signupLink} variant="secondary" size="lg">
                Cadastrar
              </Button>
            </div>

            <div className="flex items-center gap-2 text-xs font-semibold text-muted uppercase tracking-wider opacity-80">
              <span className="bg-subtle px-2 py-1 rounded border border-border">Nota</span>
              <ChevronRight size={14} className="text-border" />
              <span className="bg-subtle px-2 py-1 rounded border border-border">Brief</span>
              <ChevronRight size={14} className="text-border" />
              <span className="bg-white text-ink px-2 py-1 rounded border border-border shadow-sm">Inbox</span>
            </div>
          </div>

          <div className="relative z-10 w-full mt-8 lg:mt-0 flex justify-center lg:justify-end">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[140%] bg-primary/5 rounded-full blur-[100px] -z-10 pointer-events-none"></div>
            <HeroMock />
          </div>
        </div>
      </section>

      {/* SECTION C: SOCIAL PROOF */}
      <section className="py-10 border-b border-border bg-white">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12">
          <p className="text-xs font-bold text-muted uppercase tracking-widest whitespace-nowrap">
            Feito para executivos
          </p>
          <div className="h-4 w-px bg-border hidden md:block"></div>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { label: "Dono e prazo", icon: CheckCircle2 },
              { label: "Inbox do dia", icon: InboxIcon },
              { label: "Registro revisado", icon: FileText }
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-subtle border border-border text-ink font-medium text-sm">
                <item.icon size={14} className="text-muted" />
                {item.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION D: DOR */}
      <section className="py-20 md:py-28 px-4 bg-background">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div className="order-2 lg:order-1">
             <PainMock />
          </div>
          <div className="order-1 lg:order-2">
            <h2 className="font-display font-bold text-3xl md:text-4xl text-ink mb-6">
              Decisão sem dono vira ruído.
            </h2>
            <p className="text-lg text-muted leading-relaxed mb-8">
              O follow-up morre no WhatsApp, no e-mail e em anotações espalhadas. A reunião vira um custo caro, não um avanço real.
            </p>
            <ul className="space-y-4">
              <li className="flex gap-3 items-start">
                <div className="mt-1 w-1.5 h-1.5 rounded-full bg-red-500 shrink-0"></div>
                <p className="text-sm text-slate-600">Informação descentralizada e impossível de buscar.</p>
              </li>
              <li className="flex gap-3 items-start">
                <div className="mt-1 w-1.5 h-1.5 rounded-full bg-red-500 shrink-0"></div>
                <p className="text-sm text-slate-600">Cobranças manuais que desgastam o líder.</p>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* SECTION E: COMO FUNCIONA */}
      <section id="como-funciona" className="py-20 md:py-28 bg-white border-y border-border scroll-mt-28">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-12">
            {[
              {
                icon: FileText,
                title: "1. Capture",
                desc: "Texto rápido, ideias, resumo ou transcrição. Cole qualquer coisa e deixe a IA estruturar."
              },
              {
                icon: CheckSquare,
                title: "2. Transforme em Brief",
                desc: "Resumo executivo, decisões, ações e riscos em um cartão claro e editável."
              },
              {
                icon: InboxIcon,
                title: "3. Execute",
                desc: "Tudo cai na Inbox: hoje, atrasadas e sem prazo. O follow-up vive e respira."
              }
            ].map((step, i) => (
              <div key={i} className="flex flex-col items-start group p-6 -mx-6 rounded-2xl transition-colors hover:bg-subtle/50">
                <div className="w-12 h-12 rounded-lg bg-white border border-border shadow-sm flex items-center justify-center text-ink mb-5 group-hover:border-primary/50 group-hover:text-primary transition-colors">
                  <step.icon size={24} strokeWidth={1.5} />
                </div>
                <h3 className="font-display font-bold text-xl text-ink mb-3">{step.title}</h3>
                <p className="text-muted leading-relaxed text-sm md:text-base">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION F: INBOX */}
      <section id="inbox" className="py-20 md:py-28 px-4 scroll-mt-28">
        <div className="max-w-7xl mx-auto text-center mb-16">
          <h2 className="font-display font-bold text-3xl md:text-4xl text-ink mb-4">A Inbox que vira hábito.</h2>
          <p className="text-muted text-lg max-w-2xl mx-auto">Sem threads de e-mail intermináveis. Apenas o que precisa ser feito.</p>
        </div>
        <div className="max-w-5xl mx-auto">
             <HeroMock />
        </div>
      </section>

      {/* SECTION I: SEGURANÇA */}
      <section id="seguranca" className="py-20 md:py-24 px-4 scroll-mt-28">
        <div className="max-w-5xl mx-auto bg-white rounded-3xl p-8 md:p-16 border border-border shadow-sm">
          <div className="flex flex-col md:row gap-12 items-start">
            <div className="md:w-1/3">
              <div className="inline-flex p-3 rounded-xl bg-subtle mb-6 text-ink">
                 <Shield size={32} strokeWidth={1.5} />
              </div>
              <h2 className="font-display font-bold text-3xl text-ink mb-4">
                Discrição e controle.
              </h2>
              <p className="text-muted text-sm leading-relaxed">
                Construído pensando em ambientes onde a privacidade da informação é não-negociável.
              </p>
            </div>
            
            <div className="md:w-2/3 grid gap-6">
               {[
                 { t: "Rastreabilidade", d: "Histórico completo de quem decidiu, quando e quem executou." },
                 { t: "Privacidade", d: "Separação clara entre área pública e área autenticada." },
                 { t: "Retenção", d: "Estrutura pronta para políticas de retenção corporativa." }
               ].map((item, i) => (
                 <div key={i} className="flex gap-4 p-4 rounded-lg hover:bg-subtle/30 transition-colors border border-transparent hover:border-subtle">
                   <div className="mt-1 w-6 h-6 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center text-xs shrink-0">{i+1}</div>
                   <div>
                     <h4 className="font-bold text-ink text-sm mb-1">{item.t}</h4>
                     <span className="text-muted text-sm leading-relaxed">{item.d}</span>
                   </div>
                 </div>
               ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION J: FAQ */}
      <section id="faq" className="py-20 px-4 max-w-3xl mx-auto scroll-mt-28">
        <h2 className="font-display font-bold text-2xl md:text-3xl text-ink mb-12 text-center">Perguntas Frequentes</h2>
        <div className="space-y-4">
          {[
            { q: "Isso substitui meu CRM?", a: "Não. O PERO Brief complementa com o 'last mile' da execução diária, onde o CRM é pesado demais." },
            { q: "Preciso gravar reunião?", a: "Não obrigatório. Você pode capturar o essencial digitando notas rápidas ou colando texto." },
            { q: "Como vira tarefa?", a: "Tudo vira um item na Inbox interna. Integrações (Jira/Asana) estão no roadmap." },
            { q: "Para quem é?", a: "Executivos C-Level, líderes de produto e times que precisam de fechamento rápido." },
          ].map((faq, i) => (
            <div key={i} className="p-6 border border-border rounded-2xl hover:shadow-md hover:border-slate-300 transition-all bg-white group cursor-default">
              <h3 className="font-bold text-ink text-lg mb-2 group-hover:text-primary transition-colors">{faq.q}</h3>
              <p className="text-muted text-base leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SECTION K: CTA FINAL */}
      <section className="py-24 px-4 bg-ink text-white">
        <div className="max-w-4xl mx-auto text-center relative">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-primary/20 blur-[120px] rounded-full pointer-events-none"></div>

          <div className="relative z-10">
            <h2 className="font-display font-bold text-4xl md:text-6xl mb-6 tracking-tight text-white">
              Cole uma nota. <br />
              <span className="text-slate-400">Veja o Brief pronto.</span>
            </h2>
            
            <p className="text-slate-400 text-lg mb-10 max-w-lg mx-auto">
               Sem fricção. Sem hype. Só execução. Comece a usar o sistema operacional do follow-up.
            </p>

            <div className="flex flex-col sm:flex-row justify-center items-center gap-4 w-full sm:w-auto">
              <Button href={tourLink} size="lg" className="w-full sm:w-auto h-12 px-8 text-base bg-primary hover:bg-primary-hover border-transparent text-white shadow-xl shadow-primary/20 rounded-xl">
                Ver meu Brief em 60s
              </Button>
              <Button href={signupLink} variant="outline" size="lg" className="w-full sm:w-auto h-12 px-8 text-base text-white border-white/20 hover:bg-white/10 hover:border-white/40 rounded-xl">
                Cadastrar
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};